// CreditsView — admin gerencia o saldo de créditos pré-pago de cada
// usuário (MVP manual: aluno paga Pix, admin libera créditos).
//
// Layout: lista geral à esquerda (busca por email/nome, ordenado por saldo
// decrescente) + drawer à direita com saldo + extrato + form "Adicionar".
//
// [[project-prepaid-credits-strategy]]

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Wallet,
  X
} from "lucide-react";
import { api, ApiError } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { openAlert } from "./ConfirmPromptModal";

interface CreditRow {
  userId: string;
  email: string | null;
  name: string | null;
  balanceCents: number;
  totalToppedUpCents: number;
  totalConsumedCents: number;
  updatedAt: string;
}

const items = signal<CreditRow[]>([]);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const query = signal<string>("");
const selectedUserId = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listUserCredits(query.value.trim() || undefined);
    items.value = r.items;
    phase.value = "ready";
  } catch (err) {
    errMsg.value = err instanceof ApiError ? err.message : String(err);
    phase.value = "error";
  }
}

export function CreditsView() {
  useEffect(() => {
    void load();
  }, []);

  // Debounce de busca.
  useEffect(() => {
    const t = window.setTimeout(() => void load(), 250);
    return () => window.clearTimeout(t);
  }, [query.value]);

  return (
    <div class="space-y-4">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="relative max-w-md flex-1">
          <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Buscar por email ou nome…"
            value={query.value}
            onInput={(e) => (query.value = (e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-sm focus:border-primary focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => void load()}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
          title="Recarregar"
        >
          <RefreshCw className="size-3.5" />
        </button>
      </div>

      {phase.value === "loading" && (
        <p class="flex items-center gap-2 py-12 text-center text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      )}

      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mr-2 inline size-4" />
          {errMsg.value}
        </div>
      )}

      {phase.value === "ready" && (
        <>
          <p class="text-xs text-muted-foreground">
            {items.value.length} usuário(s). Saldo total na plataforma:{" "}
            <strong class="text-foreground">
              {formatBRL(items.value.reduce((s, r) => s + r.balanceCents, 0))}
            </strong>
          </p>
          <div class="overflow-x-auto rounded-md border border-border">
            <table class="w-full text-sm">
              <thead class="bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th class="px-3 py-2 text-left">Usuário</th>
                  <th class="px-3 py-2 text-right">Saldo</th>
                  <th class="px-3 py-2 text-right">Total recarregado</th>
                  <th class="px-3 py-2 text-right">Total consumido</th>
                  <th class="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.value.length === 0 ? (
                  <tr>
                    <td colSpan={5} class="px-3 py-8 text-center text-sm text-muted-foreground">
                      Nenhum usuário com créditos ainda.
                    </td>
                  </tr>
                ) : (
                  items.value.map((r) => (
                    <tr
                      key={r.userId}
                      class="cursor-pointer border-t border-border hover:bg-accent/40"
                      onClick={() => (selectedUserId.value = r.userId)}
                    >
                      <td class="px-3 py-2">
                        <div class="font-semibold">{r.name ?? "(sem nome)"}</div>
                        <div class="font-mono text-[11px] text-muted-foreground">
                          {r.email ?? "—"}
                        </div>
                      </td>
                      <td class="px-3 py-2 text-right">
                        <span
                          class={`font-bold tabular-nums ${
                            r.balanceCents < 0
                              ? "text-destructive"
                              : r.balanceCents < 100
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-emerald-700 dark:text-emerald-300"
                          }`}
                        >
                          {formatBRL(r.balanceCents)}
                        </span>
                      </td>
                      <td class="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {formatBRL(r.totalToppedUpCents)}
                      </td>
                      <td class="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {formatBRL(r.totalConsumedCents)}
                      </td>
                      <td class="px-3 py-2 text-right">
                        <button
                          type="button"
                          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-accent"
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selectedUserId.value && (
        <UserCreditsDrawer
          userId={selectedUserId.value}
          onClose={() => (selectedUserId.value = null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function UserCreditsDrawer({
  userId,
  onClose,
  onChanged
}: {
  userId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getUserCredits>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [amountStr, setAmountStr] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<"add" | "remove">("add");

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.getUserCredits(userId);
      setData(r);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [userId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !saving) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const amountCents = useMemo(() => parseBRL(amountStr), [amountStr]);

  async function submit() {
    if (!amountCents) {
      setErr("Informe um valor (ex: 25,00 ou 25)");
      return;
    }
    if (!desc.trim()) {
      setErr("Descreva a operação (motivo da concessão ou ajuste)");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      if (mode === "add") {
        await api.grantUserCredits(userId, {
          amountCents,
          description: desc.trim(),
          kind: "purchase"
        });
      } else {
        await api.adjustUserCredits(userId, {
          amountCents,
          description: desc.trim()
        });
      }
      setAmountStr("");
      setDesc("");
      await refresh();
      onChanged();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex h-[85vh] max-h-[760px] w-full max-w-2xl flex-col rounded-lg border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between border-b border-border px-5 py-3">
          <div class="min-w-0">
            <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Créditos
            </div>
            {data ? (
              <div>
                <div class="truncate text-base font-bold">{data.user.name}</div>
                <div class="truncate font-mono text-[11px] text-muted-foreground">{data.user.email}</div>
              </div>
            ) : (
              <div class="text-sm text-muted-foreground">Carregando…</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </header>

        {loading && (
          <p class="flex items-center justify-center gap-2 px-4 py-12 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Carregando…
          </p>
        )}

        {data && (
          <div class="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
            <div class="grid grid-cols-3 gap-2">
              <KpiCard
                label="Saldo atual"
                value={formatBRL(data.credits.balanceCents)}
                tone={
                  data.credits.balanceCents < 0
                    ? "danger"
                    : data.credits.balanceCents < 100
                    ? "warn"
                    : "ok"
                }
              />
              <KpiCard
                label="Total recarregado"
                value={formatBRL(data.credits.totalToppedUpCents)}
                tone="muted"
              />
              <KpiCard
                label="Total consumido"
                value={formatBRL(data.credits.totalConsumedCents)}
                tone="muted"
              />
            </div>

            {/* Form de adicionar/remover */}
            <div class="rounded-md border border-border bg-background/60 p-3">
              <div class="mb-2 flex items-center gap-2">
                <Wallet className="size-4 text-primary" />
                <h3 class="text-sm font-bold">Lançar movimento</h3>
              </div>
              <div class="flex gap-1.5 text-xs">
                <button
                  type="button"
                  onClick={() => setMode("add")}
                  class={`inline-flex flex-1 items-center justify-center gap-1 rounded-md border px-2 py-1.5 font-semibold transition-colors ${
                    mode === "add"
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "border-border bg-background hover:bg-accent"
                  }`}
                >
                  <Plus className="size-3.5" />
                  Adicionar (recarga)
                </button>
                <button
                  type="button"
                  onClick={() => setMode("remove")}
                  class={`inline-flex flex-1 items-center justify-center gap-1 rounded-md border px-2 py-1.5 font-semibold transition-colors ${
                    mode === "remove"
                      ? "border-destructive/40 bg-destructive/10 text-destructive"
                      : "border-border bg-background hover:bg-accent"
                  }`}
                >
                  <ArrowDownCircle className="size-3.5" />
                  Remover (ajuste)
                </button>
              </div>
              <div class="mt-3 grid gap-2">
                <label class="block">
                  <span class="mb-0.5 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Valor em BRL
                  </span>
                  <input
                    type="text"
                    value={amountStr}
                    onInput={(e) => setAmountStr((e.target as HTMLInputElement).value)}
                    placeholder="25,00"
                    class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
                    inputMode="decimal"
                  />
                  {amountStr.trim() && amountCents !== null && (
                    <span class="mt-0.5 block text-[10px] text-muted-foreground">
                      = {formatBRL(amountCents)} ({amountCents} centavos)
                    </span>
                  )}
                </label>
                <label class="block">
                  <span class="mb-0.5 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {mode === "add" ? "Motivo / referência" : "Motivo do ajuste"}
                  </span>
                  <input
                    type="text"
                    value={desc}
                    onInput={(e) => setDesc((e.target as HTMLInputElement).value)}
                    placeholder={
                      mode === "add"
                        ? "Pix recebido 19/05 — comprovante 12345"
                        : "Estorno por erro de cobrança"
                    }
                    maxLength={500}
                    class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
                  />
                </label>
                {err && (
                  <p class="text-xs text-destructive">
                    <AlertCircle className="mr-1 inline size-3" /> {err}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => void submit()}
                  disabled={saving || !amountCents || !desc.trim()}
                  class={`mt-1 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-bold transition-colors disabled:opacity-50 ${
                    mode === "add"
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "bg-destructive text-destructive-foreground hover:brightness-110"
                  }`}
                >
                  {saving ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : mode === "add" ? (
                    <Plus className="size-3.5" />
                  ) : (
                    <ArrowDownCircle className="size-3.5" />
                  )}
                  {mode === "add" ? "Liberar créditos" : "Lançar débito"}
                </button>
              </div>
            </div>

            {/* Extrato */}
            <div>
              <h3 class="mb-2 text-sm font-bold">Extrato — últimas 100 entradas</h3>
              {data.ledger.length === 0 ? (
                <p class="text-xs text-muted-foreground">Sem movimentos ainda.</p>
              ) : (
                <ul class="space-y-1">
                  {data.ledger.map((l) => (
                    <li
                      key={l.id}
                      class="flex items-start gap-2 rounded-md border border-border bg-background/60 px-3 py-2 text-xs"
                    >
                      {l.deltaCents >= 0 ? (
                        <ArrowUpCircle className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <ArrowDownCircle className="size-4 shrink-0 text-destructive" />
                      )}
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-1.5">
                          <span
                            class={`font-bold ${
                              l.deltaCents >= 0
                                ? "text-emerald-700 dark:text-emerald-300"
                                : "text-destructive"
                            }`}
                          >
                            {l.deltaCents >= 0 ? "+" : ""}
                            {formatBRL(l.deltaCents)}
                          </span>
                          <KindChip kind={l.kind} />
                        </div>
                        {l.description && (
                          <div class="mt-0.5 text-muted-foreground">{l.description}</div>
                        )}
                      </div>
                      <span class="shrink-0 font-mono text-[10px] text-muted-foreground">
                        {new Date(l.createdAt).toLocaleString("pt-BR")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone: "ok" | "warn" | "danger" | "muted";
}) {
  const toneCls =
    tone === "ok"
      ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"
      : tone === "warn"
      ? "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300"
      : tone === "danger"
      ? "border-destructive/30 bg-destructive/5 text-destructive"
      : "border-border bg-background/60";
  return (
    <div class={`rounded-md border px-3 py-2 ${toneCls}`}>
      <div class="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div class="text-base font-bold tabular-nums">{value}</div>
    </div>
  );
}

function KindChip({ kind }: { kind: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    purchase: { label: "Recarga", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
    initial_bonus: { label: "Bônus inicial", cls: "bg-violet-500/15 text-violet-700 dark:text-violet-300" },
    bonus: { label: "Bônus", cls: "bg-violet-500/15 text-violet-700 dark:text-violet-300" },
    consume: { label: "Consumo IA", cls: "bg-muted text-muted-foreground" },
    adjust: { label: "Ajuste", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
    refund: { label: "Estorno", cls: "bg-sky-500/15 text-sky-700 dark:text-sky-300" },
    platform_monthly_fee: {
      label: "Mensalidade",
      cls: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300"
    }
  };
  const meta = map[kind] ?? { label: kind, cls: "bg-muted text-muted-foreground" };
  return (
    <span class={`inline-flex items-center rounded px-1.5 py-0 text-[9px] font-semibold ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

/** Converte string "25,00" / "25.50" / "25" para centavos. Aceita vírgula
 *  ou ponto como separador decimal. Devolve null se inválido. */
function parseBRL(s: string): number | null {
  if (!s) return null;
  const normalized = s.replace(/\./g, "").replace(",", ".").trim();
  const n = Number(normalized);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}
